import {
  createBaaAgreement,
  fetchBaaStatus,
  messageFromAuthApiErrorBody,
  refreshBaaStatus,
} from "@app/agreements";
import { selectEnv } from "@app/config";
import { fileDate, prettyEnglishDateTimeTzFull } from "@app/date";
import {
  fetchActivePlans,
  fetchPlans,
  getStackType,
  selectFirstActivePlan,
  selectIsAccountOwner,
  selectPlanByActiveId,
  selectStacksByOrgAsList,
} from "@app/deploy";
import { selectOrganizationSelected } from "@app/organizations";
import { useDispatch, useLoader, useQuery, useSelector } from "@app/react";
import { schema } from "@app/schema";
import { selectAccessToken } from "@app/token";
import type { AgreementStatus, AgreementSummary } from "@app/types";
import { selectCurrentUser } from "@app/users";
import { useEffect, useMemo, useRef, useState } from "react";
import { HeroBgLayout } from "../layouts";
import {
  ActionBar,
  Banner,
  Box,
  Button,
  ExternalLink,
  FormGroup,
  Group,
  IconCheckCircle,
  IconDownload,
  IconLock,
  Input,
  TitleBar,
  tokens,
} from "../shared";
import { LoadingSpinner } from "../shared/loading";
import {
  type BaaSignerFlow,
  baaStatusPollIntervalMs,
  baaStatusPollMaxMs,
  clearExternalSignerEmail,
  clearSelfSignerState,
  readExternalSignerEmail,
  readSelfSignerState,
  shouldPollBaaStatus,
  shouldResetSignerState,
  shouldShowSelfSigningPending,
  writeExternalSignerEmail,
  writeSelfSignerState,
} from "./settings-legal-agreements-flow";

function loaderErrorText(
  loader: {
    message?: string;
    meta?: { message?: string; error?: unknown; json?: { error?: unknown } };
  },
  fallback: string,
) {
  const parsedMetaError = messageFromAuthApiErrorBody(loader.meta?.error);
  const parsedMetaJsonError = messageFromAuthApiErrorBody(
    loader.meta?.json?.error,
  );
  return (
    loader.message ||
    parsedMetaError ||
    parsedMetaJsonError ||
    loader.meta?.message ||
    fallback
  );
}

function createAgreementErrorText(loader: {
  message?: string;
  meta?: { message?: string; error?: unknown; json?: { error?: unknown } };
}) {
  const msg = loaderErrorText(loader, "Failed to create agreement");
  if (/access denied|forbidden/i.test(msg)) {
    return "Only Account Owners can sign Business Associate Agreements";
  }
  return msg;
}

const emailMismatchError = "Signer email addresses must match.";
const outForSignatureStatusText =
  "The BAA has been sent via email for signature.";
type CreateBaaAgreementAction = ReturnType<typeof createBaaAgreement>;

const SettingsLegalAgreementsContent = () => {
  const dispatch = useDispatch();
  const env = useSelector(selectEnv);
  const accessToken = useSelector(selectAccessToken);
  const org = useSelector(selectOrganizationSelected);
  const user = useSelector(selectCurrentUser);

  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isAwaitingSignedStatus, setIsAwaitingSignedStatus] = useState(false);
  const [showRenewForm, setShowRenewForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [signerFlow, setSignerFlow] = useState<BaaSignerFlow>(null);
  const [pendingSignerFlow, setPendingSignerFlow] =
    useState<BaaSignerFlow>(null);
  const [pendingSignerEmail, setPendingSignerEmail] = useState<string | null>(
    null,
  );
  const [externalSignerEmail, setExternalSignerEmail] = useState<string | null>(
    null,
  );
  const [pollStartedAtMs, setPollStartedAtMs] = useState<number | null>(null);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [isSigningPopupBlocked, setIsSigningPopupBlocked] = useState(false);
  const openedSigningUrlRef = useRef<string | null>(null);
  const pendingSelfSignTabRef = useRef<Window | null>(null);
  const attemptedSelfSignTabOpenRef = useRef(false);

  const orgId = org.id;
  const currentUserId = `${user.id}`;
  const prevUserIdRef = useRef(currentUserId);
  const isAccountOwner = useSelector((s) => selectIsAccountOwner(s, { orgId }));

  // API actions
  const fetchAction = useMemo(() => fetchBaaStatus({ orgId }), [orgId]);
  const [submittedCreateAction, setSubmittedCreateAction] =
    useState<CreateBaaAgreementAction>(() =>
      createBaaAgreement({
        orgId,
        email: "",
        firstName: "",
        lastName: "",
      }),
    );
  const refreshAction = useMemo(() => refreshBaaStatus({ orgId }), [orgId]);
  const createActionKey = submittedCreateAction.payload.key;

  useEffect(() => {
    setSubmittedCreateAction(
      createBaaAgreement({
        orgId,
        email: "",
        firstName: "",
        lastName: "",
      }),
    );
    setEmail("");
    setConfirmEmail("");
    setFirstName("");
    setLastName("");
    setError(null);
    setIsAwaitingSignedStatus(false);
    setShowRenewForm(false);
    setIsDownloadingPdf(false);
    setSignerFlow(null);
    setPendingSignerFlow(null);
    setPendingSignerEmail(null);
    setExternalSignerEmail(null);
    setPollStartedAtMs(null);
    setPollTimedOut(false);
    setIsSigningPopupBlocked(false);
    openedSigningUrlRef.current = null;
    pendingSelfSignTabRef.current = null;
    attemptedSelfSignTabOpenRef.current = false;
  }, [orgId]);

  // Loaders
  const fetchLoader = useLoader(fetchAction);
  const createLoader = useLoader(submittedCreateAction);
  const refreshLoader = useLoader(refreshAction);

  // Get cached responses
  const fetchData = useSelector((s) =>
    schema.cache.selectById(s, { id: fetchAction.payload.key }),
  ) as AgreementSummary | undefined;
  const createData = useSelector((s) =>
    schema.cache.selectById(s, { id: createActionKey }),
  ) as AgreementSummary | undefined;
  const refreshData = useSelector((s) =>
    schema.cache.selectById(s, { id: refreshAction.payload.key }),
  ) as AgreementSummary | undefined;
  const fetchResolvedAsNone = fetchData?.status === "NONE";
  const currentSummary = useMemo(() => {
    const hasActiveSignerSession =
      signerFlow != null || pendingSignerFlow != null || isAwaitingSignedStatus;
    const refreshIsTerminal =
      refreshData?.status === "SIGNED" || refreshData?.status === "CANCELLED";
    const fetchIsTerminal =
      fetchData?.status === "SIGNED" || fetchData?.status === "CANCELLED";

    // Always honor terminal statuses from refresh/fetch first so stale create
    // cache entries cannot mask a completed or cancelled agreement.
    if (refreshIsTerminal) {
      return refreshData;
    }
    // Keep in-progress signing UX visible while a signer session is active.
    if (fetchIsTerminal && !hasActiveSignerSession) {
      return fetchData;
    }
    if (signerFlow === "self" || pendingSignerFlow === "self") {
      if (!isAwaitingSignedStatus) {
        if (createData && createData.status === "OUT_FOR_SIGNATURE") {
          return createData;
        }
        if (refreshData && refreshData.status === "OUT_FOR_SIGNATURE") {
          return refreshData;
        }
        if (fetchData && fetchData.status === "OUT_FOR_SIGNATURE") {
          return fetchData;
        }
      } else {
        if (fetchIsTerminal && fetchData) return fetchData;
        if (refreshData && refreshData.status !== "NONE") return refreshData;
        if (fetchData && fetchData.status !== "NONE") return fetchData;
      }
    }
    if (isAwaitingSignedStatus && fetchData && fetchData.status !== "NONE") {
      return fetchData;
    }
    if (createData && createData.status !== "NONE") return createData;
    if (refreshData && refreshData.status !== "NONE") return refreshData;
    if (fetchData && fetchData.status !== "NONE") return fetchData;
    return fetchData ?? refreshData ?? createData ?? null;
  }, [
    fetchData,
    refreshData,
    createData,
    signerFlow,
    pendingSignerFlow,
    isAwaitingSignedStatus,
  ]);

  // Handle errors
  useEffect(() => {
    if (fetchLoader.isError && !fetchResolvedAsNone) {
      setError(
        loaderErrorText(fetchLoader, "Failed to fetch agreement status"),
      );
    } else if (fetchLoader.isSuccess || fetchResolvedAsNone) {
      setError(null);
    }
  }, [
    fetchLoader.isError,
    fetchLoader.isSuccess,
    fetchLoader.message,
    fetchLoader.meta,
    fetchResolvedAsNone,
  ]);

  useEffect(() => {
    if (createLoader.isError) {
      pendingSelfSignTabRef.current?.close();
      pendingSelfSignTabRef.current = null;
      attemptedSelfSignTabOpenRef.current = false;
      setPendingSignerFlow(null);
      setPendingSignerEmail(null);
      setError(createAgreementErrorText(createLoader));
    } else if (createLoader.isSuccess) {
      if (pendingSignerFlow === "external" && pendingSignerEmail) {
        setSignerFlow("external");
        setExternalSignerEmail(pendingSignerEmail);
      } else if (pendingSignerFlow === "self") {
        setSignerFlow("self");
        setExternalSignerEmail(null);
      }
      setPendingSignerFlow(null);
      setPendingSignerEmail(null);
      setError(null);
    }
  }, [
    createLoader.isError,
    createLoader.isSuccess,
    createLoader.message,
    createLoader.meta,
    pendingSignerFlow,
    pendingSignerEmail,
  ]);

  useEffect(() => {
    if (refreshLoader.isError) {
      setError(loaderErrorText(refreshLoader, "Failed to refresh status"));
    } else if (refreshLoader.isSuccess) {
      setError(null);
    }
  }, [
    refreshLoader.isError,
    refreshLoader.isSuccess,
    refreshLoader.message,
    refreshLoader.meta,
  ]);

  const summaryStatus = currentSummary?.status;
  const status: AgreementStatus = summaryStatus || "NONE";
  const signingUrl = currentSummary?.signingUrl;
  const isFetching =
    fetchLoader.isLoading || createLoader.isLoading || refreshLoader.isLoading;
  const isSummaryResolved = currentSummary != null || fetchResolvedAsNone;
  const normalizedEmail = email.trim().toLowerCase();
  const normalizedConfirmEmail = confirmEmail.trim().toLowerCase();
  const normalizedCurrentUserEmail = user.email.trim().toLowerCase();
  const isCurrentUserSignerEmail =
    normalizedEmail !== "" && normalizedEmail === normalizedCurrentUserEmail;
  const emailsMatch =
    normalizedEmail !== "" && normalizedEmail === normalizedConfirmEmail;
  const isExternalSignerEmail =
    normalizedEmail !== "" && !isCurrentUserSignerEmail;

  useEffect(() => {
    if (emailsMatch && error === emailMismatchError) {
      setError(null);
    }
  }, [emailsMatch, error]);

  const createAgreement = () => {
    if (!isAccountOwner) return;
    if (!email || !confirmEmail || !firstName.trim() || !lastName.trim())
      return;
    if (!emailsMatch) {
      setError(emailMismatchError);
      return;
    }
    setError(null);
    setPollTimedOut(false);
    setPollStartedAtMs(null);
    setIsSigningPopupBlocked(false);
    if (isExternalSignerEmail) {
      setPendingSignerFlow("external");
      setPendingSignerEmail(email.trim());
      pendingSelfSignTabRef.current = null;
      attemptedSelfSignTabOpenRef.current = false;
    } else {
      setPendingSignerFlow("self");
      setPendingSignerEmail(null);
      attemptedSelfSignTabOpenRef.current = true;
      const preOpenedTab = window.open("", "_blank");
      pendingSelfSignTabRef.current = preOpenedTab;
      setIsSigningPopupBlocked(!preOpenedTab);
    }
    const action = createBaaAgreement({
      orgId,
      email,
      firstName,
      lastName,
    });
    setSubmittedCreateAction(action);
    dispatch(action);
  };

  const refresh = () => {
    setPollTimedOut(false);
    setPollStartedAtMs(Date.now());
    dispatch(refreshAction);
  };

  const downloadSignedPdf = async () => {
    if (!accessToken || !env.authUrl) return;
    setIsDownloadingPdf(true);
    setError(null);
    try {
      const base = env.authUrl.replace(/\/$/, "");
      const url =
        currentSummary?.downloadUrl ||
        `${base}/organizations/${encodeURIComponent(orgId)}/baa/download`;
      const resp = await fetch(url, {
        credentials: "include",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!resp.ok) {
        let msg = "Failed to download signed agreement";
        try {
          const body: unknown = await resp.json();
          const parsed = messageFromAuthApiErrorBody(body);
          if (parsed) msg = parsed;
        } catch {
          /* ignore */
        }
        setError(msg);
        return;
      }
      const blob = await resp.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = blobUrl;
      a.download = `aptible-baa-${orgId}-${fileDate(new Date().toISOString())}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      a.remove();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to download signed agreement",
      );
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  // Initial status load
  useEffect(() => {
    if (!orgId) return;
    dispatch(fetchAction);
  }, [orgId, dispatch, fetchAction]);

  useEffect(() => {
    if (prevUserIdRef.current === currentUserId) return;
    const previousUserId = prevUserIdRef.current;
    prevUserIdRef.current = currentUserId;
    // Skip boot-time hydration transition from empty -> first loaded user.
    if (!previousUserId) return;

    clearSelfSignerState(orgId, previousUserId);
    setIsAwaitingSignedStatus(false);
    setSignerFlow(null);
    setPendingSignerFlow(null);
    setPendingSignerEmail(null);
    setExternalSignerEmail(null);
    setIsSigningPopupBlocked(false);
    openedSigningUrlRef.current = null;
    pendingSelfSignTabRef.current = null;
    attemptedSelfSignTabOpenRef.current = false;
  }, [orgId, currentUserId]);

  useEffect(() => {
    if (!orgId) return;
    const persisted = readExternalSignerEmail(orgId);
    if (persisted) {
      setSignerFlow("external");
      setExternalSignerEmail(persisted);
    }
  }, [orgId]);

  useEffect(() => {
    if (!orgId || !currentUserId || !isSummaryResolved) return;
    const persisted = readSelfSignerState(orgId, currentUserId);
    if (!persisted) return;
    if (status !== "OUT_FOR_SIGNATURE") return;
    setSignerFlow("self");
    setExternalSignerEmail(null);
    setIsAwaitingSignedStatus(false);
    setIsSigningPopupBlocked(persisted.popupBlocked);
  }, [orgId, currentUserId, isSummaryResolved, status]);

  useEffect(() => {
    if (!orgId) return;
    if (!isSummaryResolved) return;
    if (signerFlow === "external" && externalSignerEmail) {
      writeExternalSignerEmail(orgId, externalSignerEmail);
    } else {
      clearExternalSignerEmail(orgId);
    }

    if (signerFlow === "self" && status === "OUT_FOR_SIGNATURE") {
      writeSelfSignerState(orgId, currentUserId, {
        popupBlocked: isSigningPopupBlocked,
        agreementId: currentSummary?.agreementId,
      });
    } else if (status !== "OUT_FOR_SIGNATURE" || signerFlow === "external") {
      clearSelfSignerState(orgId, currentUserId);
    }
  }, [
    orgId,
    currentUserId,
    signerFlow,
    externalSignerEmail,
    isSummaryResolved,
    status,
    isSigningPopupBlocked,
    currentSummary?.agreementId,
  ]);

  // Listen for postMessage from callback page after Adobe redirect.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.source !== "adobeSign" || e.data?.event !== "completed") {
        return;
      }
      const as = e.data?.agreementStatus as string | undefined;
      if (as != null && as !== "" && as !== "completed") {
        return;
      }
      setSignerFlow("self");
      setExternalSignerEmail(null);
      setIsAwaitingSignedStatus(true);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  useEffect(() => {
    const shouldResetNone = summaryStatus === "NONE" && fetchLoader.isSuccess;
    const shouldResetStatus =
      status !== "NONE" && shouldResetSignerState(status);
    if (shouldResetNone || shouldResetStatus) {
      setIsAwaitingSignedStatus(false);
      setSignerFlow(null);
      setExternalSignerEmail(null);
      setShowRenewForm(false);
      setPollStartedAtMs(null);
      setPollTimedOut(false);
      setIsSigningPopupBlocked(false);
      openedSigningUrlRef.current = null;
      pendingSelfSignTabRef.current = null;
      attemptedSelfSignTabOpenRef.current = false;
      if (orgId) {
        clearExternalSignerEmail(orgId);
        clearSelfSignerState(orgId, currentUserId);
      }
    }
  }, [status, summaryStatus, orgId, fetchLoader.isSuccess, currentUserId]);

  useEffect(() => {
    if (
      signerFlow !== "self" ||
      status !== "OUT_FOR_SIGNATURE" ||
      !signingUrl
    ) {
      return;
    }
    if (openedSigningUrlRef.current === signingUrl) return;
    openedSigningUrlRef.current = signingUrl;
    let didOpen = false;
    const pendingTab = pendingSelfSignTabRef.current;
    if (pendingTab && !pendingTab.closed) {
      try {
        pendingTab.location.href = signingUrl;
        didOpen = true;
      } catch {
        didOpen = false;
      }
    }
    pendingSelfSignTabRef.current = null;

    if (!didOpen && !attemptedSelfSignTabOpenRef.current) {
      const tab = window.open(signingUrl, "_blank", "noopener,noreferrer");
      didOpen = !!tab;
    }
    setIsSigningPopupBlocked(!didOpen);
    attemptedSelfSignTabOpenRef.current = false;
  }, [signerFlow, status, signingUrl]);

  const isPollingBaaStatus =
    !pollTimedOut &&
    shouldPollBaaStatus({
      status,
      signerFlow,
      isAwaitingSignedStatus,
      pollStartedAtMs,
    });

  useEffect(() => {
    if (!isPollingBaaStatus || pollStartedAtMs != null) return;
    setPollStartedAtMs(Date.now());
    setPollTimedOut(false);
  }, [isPollingBaaStatus, pollStartedAtMs]);

  // Poll while waiting for signature completion from either flow.
  useEffect(() => {
    if (!isPollingBaaStatus || pollStartedAtMs == null) return;
    dispatch(fetchAction);
    const id = window.setInterval(() => {
      if (Date.now() - pollStartedAtMs >= baaStatusPollMaxMs) {
        setPollTimedOut(true);
        window.clearInterval(id);
        return;
      }
      dispatch(fetchAction);
    }, baaStatusPollIntervalMs);
    return () => window.clearInterval(id);
  }, [isPollingBaaStatus, pollStartedAtMs, dispatch, fetchAction]);

  useEffect(() => {
    if (!pollTimedOut) return;
    setPollStartedAtMs(null);
  }, [pollTimedOut]);

  // No row in DB for this org → API returns status NONE; signed orgs can also request a new agreement.
  const showForm =
    isSummaryResolved &&
    (status === "NONE" ||
      ((status === "SIGNED" || status === "CANCELLED") && showRenewForm));
  // In-flight self-signature: show pending state only when we have a valid signing URL.
  const showSelfSigningPending = shouldShowSelfSigningPending({
    status,
    signingUrl,
    signerFlow,
  });
  const showEmailSentMessage =
    status === "OUT_FOR_SIGNATURE" &&
    signerFlow === "external" &&
    !!externalSignerEmail &&
    !isAwaitingSignedStatus;
  const showPostSignLoading =
    isAwaitingSignedStatus &&
    signerFlow !== "self" &&
    status !== "SIGNED" &&
    status !== "CANCELLED";
  const showGenericPendingMessage =
    status === "OUT_FOR_SIGNATURE" &&
    !showSelfSigningPending &&
    !showEmailSentMessage &&
    !showPostSignLoading &&
    pendingSignerFlow !== "self";
  const isTerminal =
    isSummaryResolved && (status === "SIGNED" || status === "CANCELLED");
  const showHeaderStartNewBaaAction = isTerminal && !showRenewForm;

  return (
    <Group>
      <div className="flex justify-between">
        <TitleBar description="Terms, BAAs, and compliance documents for your organization.">
          Legal & Compliance
        </TitleBar>
        {showHeaderStartNewBaaAction ? (
          <ActionBar>
            <Button
              variant="white"
              size="sm"
              onClick={() => setShowRenewForm(true)}
            >
              Start New BAA
            </Button>
          </ActionBar>
        ) : null}
      </div>

      {error ? (
        <Banner variant="error" className="w-full">
          {error}
        </Banner>
      ) : null}

      {!isSummaryResolved && fetchLoader.isLoading ? (
        <div className="w-full py-8" aria-live="polite">
          <LoadingSpinner variant="lg" className="text-gray-500" />
        </div>
      ) : null}

      {showSelfSigningPending ? (
        <div className="flex w-full flex-col gap-3">
          <Banner variant="info" className="w-full">
            {outForSignatureStatusText}
          </Banner>
          <p className={tokens.type["small lighter"]}>
            Complete your signature in the Adobe Sign tab, then return here
            while we refresh the status automatically.
          </p>
          {isSigningPopupBlocked ? (
            <Banner variant="warning" className="w-full">
              Your browser blocked opening Adobe Sign in a new tab. Use the link
              below to continue signing.
            </Banner>
          ) : null}
          <a
            href={signingUrl}
            target="_blank"
            rel="noreferrer"
            className="text-indigo"
          >
            Open signing page
          </a>
        </div>
      ) : null}

      {showEmailSentMessage ? (
        <div className="flex w-full flex-col gap-3">
          <Banner variant="info" className="w-full">
            {outForSignatureStatusText}
          </Banner>
        </div>
      ) : null}

      {showGenericPendingMessage ? (
        <div className="flex w-full flex-col gap-3">
          <Banner variant="info" className="w-full">
            {outForSignatureStatusText}
          </Banner>
        </div>
      ) : null}

      {showPostSignLoading ? (
        <div
          className="flex w-full max-w-2xl flex-col items-start gap-3 rounded-lg border border-black-100 bg-white p-6 shadow-sm"
          aria-live="polite"
        >
          <LoadingSpinner variant="lg" className="text-gray-500" />
          <p className={`${tokens.type["small semibold darker"]}`}>
            Processing your signature…
          </p>
          <p className={`${tokens.type["small lighter"]}`}>
            This usually takes a few seconds. You can leave this page open—we’ll
            refresh automatically.
          </p>
          <Button
            variant="white"
            size="sm"
            onClick={refresh}
            isLoading={isFetching}
          >
            Refresh status
          </Button>
        </div>
      ) : null}

      {isTerminal ? (
        <div className="w-full">
          {status === "SIGNED" ? (
            <Box size="md" className="max-w-3xl">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Agreements
              </h2>
              <div className="mt-4 border-t border-black-100 pt-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="min-w-0 flex-1 pr-2">
                    <div className={tokens.type["small semibold darker"]}>
                      Business Associate Agreement
                    </div>
                    <p className={`${tokens.type["small lighter"]} mt-1`}>
                      {currentSummary?.signedAt
                        ? `Signed on ${prettyEnglishDateTimeTzFull(currentSummary.signedAt, "local")}`
                        : currentSummary?.updatedAt
                          ? `Signed on ${prettyEnglishDateTimeTzFull(currentSummary.updatedAt, "local")}`
                          : "Signed and on file."}
                    </p>
                  </div>
                  <div
                    className="flex shrink-0 items-center gap-3"
                    aria-label="Agreement signed"
                  >
                    <IconCheckCircle variant="sm" color="#00633F" aria-hidden />
                    <Button
                      variant="white"
                      size="sm"
                      onClick={() => void downloadSignedPdf()}
                      isLoading={isDownloadingPdf}
                      disabled={!accessToken}
                    >
                      <IconDownload className="mr-1.5" variant="sm" />
                      Download
                    </Button>
                  </div>
                </div>
              </div>
            </Box>
          ) : (
            <Banner variant="warning" className="w-full">
              BAA was cancelled or expired. You can request a new BAA.
            </Banner>
          )}
        </div>
      ) : null}

      {showForm ? (
        <Box>
          <Group>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                createAgreement();
              }}
            >
              <Group>
                <div className="space-y-2">
                  <h2 className={tokens.type.h2}>
                    Business Associate Agreement
                  </h2>
                  <p className={tokens.type["small lighter"]}>
                    A{" "}
                    <ExternalLink href="https://www.aptible.com/hipaa/baa">
                      Business Associate Agreement (BAA)
                    </ExternalLink>{" "}
                    is a required legal contract under HIPAA between a covered
                    entity or business associate and any vendor that creates,
                    receives, transmits, or maintains protected health
                    information on their behalf. An Account Owner can kickoff
                    the signing process for the BAA in-app by submitting the
                    form below. Aptible will send the agreement to the signer
                    via email.
                  </p>
                </div>
                {!isAccountOwner ? (
                  <Banner variant="warning" className="w-full">
                    Only Account Owners can sign Business Associate Agreements.
                  </Banner>
                ) : null}

                <FormGroup label="First Name" htmlFor="signer-first-name">
                  <Input
                    id="signer-first-name"
                    name="signer-first-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.currentTarget.value)}
                    disabled={!isAccountOwner}
                    autoComplete="given-name"
                  />
                </FormGroup>

                <FormGroup label="Last Name" htmlFor="signer-last-name">
                  <Input
                    id="signer-last-name"
                    name="signer-last-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.currentTarget.value)}
                    disabled={!isAccountOwner}
                    autoComplete="family-name"
                  />
                </FormGroup>

                <FormGroup label="Signer Email" htmlFor="signer-email">
                  <Input
                    id="signer-email"
                    name="signer-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.currentTarget.value)}
                    disabled={!isAccountOwner}
                    autoComplete="email"
                  />
                </FormGroup>

                <FormGroup
                  label="Confirm Signer Email"
                  htmlFor="signer-email-confirm"
                >
                  <Input
                    id="signer-email-confirm"
                    name="signer-email-confirm"
                    type="email"
                    value={confirmEmail}
                    onChange={(e) => setConfirmEmail(e.currentTarget.value)}
                    disabled={!isAccountOwner}
                    autoComplete="email"
                  />
                </FormGroup>

                <Group variant="horizontal" size="sm">
                  <Button
                    type="submit"
                    isLoading={isFetching}
                    disabled={
                      !isAccountOwner ||
                      !email ||
                      !confirmEmail ||
                      !emailsMatch ||
                      !firstName.trim() ||
                      !lastName.trim()
                    }
                  >
                    Review & Sign BAA
                  </Button>
                </Group>
              </Group>
            </form>
          </Group>
        </Box>
      ) : null}
    </Group>
  );
};

export const SettingsLegalAgreementsPage = () => {
  const org = useSelector(selectOrganizationSelected);
  const orgId = org.id;
  useQuery(fetchPlans());
  useQuery(fetchActivePlans({ orgId }));
  const activePlan = useSelector(selectFirstActivePlan);
  const plan = useSelector((s) =>
    selectPlanByActiveId(s, { id: activePlan.planId }),
  );
  const stacks = useSelector(selectStacksByOrgAsList);
  const hasDedicatedStack = stacks.some((s) => getStackType(s) === "dedicated");
  const isBaaEligible =
    hasDedicatedStack ||
    plan.name === "production" ||
    plan.name === "enterprise";

  if (!isBaaEligible) {
    return (
      <HeroBgLayout width={1200} showOrgPicker>
        <Group>
          <h1 className={`${tokens.type.h1} text-center`}>
            Legal & Compliance
          </h1>
          <Group className="items-center">
            <div className="max-w-2xl">
              <div className="flex items-center justify-center gap-2 p-8 bg-gray-50 rounded-lg">
                <IconLock variant="sm" className="opacity-60" />
                <p className="text-gray-600">
                  Signing a Business Associate Agreement requires a Production
                  or Enterprise plan.
                </p>
              </div>
            </div>
          </Group>
        </Group>
      </HeroBgLayout>
    );
  }

  return <SettingsLegalAgreementsContent />;
};
